/**
 * GeoCurrency - Point d'entrée de l'application SPA
 * Application de conversion de devises basée sur la géolocalisation
 *
 * Projet R3.01 - Développement Web
 * Architecture MVC en JavaScript Vanilla avec SPA
 *
 * @author [Votre nom]
 * @version 2.0.0
 */

// Import des styles
import './styles/main.css';
import './styles/google-converter.css';

// Import du Router
import { Router } from './utils/Router.js';
import { t } from './utils/i18n.js';

// Import des pages
import { MapPage, ConverterPage, SettingsPage } from './pages/index.js';

// Import des services
import { CountryService } from './services/CountryService.js';
import { ExchangeRateService } from './services/ExchangeRateService.js';
import { GeoLocationService } from './services/GeoLocationService.js';

/**
 * Classe principale de l'application GeoCurrency SPA
 * @class GeoCurrencyApp
 */
class GeoCurrencyApp {
    /**
     * Crée une instance de l'application
     */
    constructor() {
        this._router = null;
        this._services = {};
        this._sharedState = {
            countries: [],
            selectedCountry: null,
            userLocation: null,
            conversionHistory: []
        };
        this._isReady = false;
    }

    /**
     * Initialise l'application
     */
    async init() {
        console.log('[GeoCurrency] Initialisation...');
        this._showLoader('Initialisation de l\'application...');

        try {
            // Initialise les services
            this._initServices();

            // Configure le thème
            this._initTheme();

            // Traduction de l'interface
            this._localizeUI();

            // Configure la sidebar
            this._initSidebar();
            
            // Écoute les changements de langue pour un rafraîchissement fluide
            window.addEventListener('geocurrency:language-change', () => this._handleLanguageChange());

            // Configure le router
            this._initRouter();

            // Charge les données initiales
            await this._loadInitialData();

            // Démarre le router
            this._router.start();

            this._isReady = true;
            console.log('[GeoCurrency] Application prête !');

        } catch (error) {
            console.error('[GeoCurrency] Erreur d\'initialisation:', error);
            this._showFatalError(error);
        } finally {
            this._hideLoader();
        }
    }

    /**
     * Initialise les services
     * @private
     */
    _initServices() {
        this._services = {
            country: new CountryService(),
            exchangeRate: new ExchangeRateService(),
            geoLocation: new GeoLocationService()
        };
    }

    /**
     * Initialise le router SPA
     * @private
     */
    _initRouter() {
        this._router = new Router('main-content');

        // Page Carte
        this._router.register('/map', () => {
            return new MapPage({
                services: this._services,
                state: this._sharedState,
                onCountrySelect: (country) => this._handleCountrySelect(country)
            });
        });

        // Page Convertisseur
        this._router.register('/converter', () => {
            return new ConverterPage({
                services: this._services,
                state: this._sharedState,
                onHistoryUpdate: (history) => this._handleHistoryUpdate(history)
            });
        });

        // Page Paramètres
        this._router.register('/settings', () => {
            return new SettingsPage({
                services: this._services,
                state: this._sharedState,
                onSettingsChange: (settings) => this._handleSettingsChange(settings)
            });
        });

        // Route par défaut
        this._router.setDefault('/map');

        // Écoute les changements de route pour mettre à jour la nav
        window.addEventListener('hashchange', () => {
            this._updateActiveNav();
        });
    }

    /**
     * Charge les données initiales
     * @private
     */
    async _loadInitialData() {
        this._showLoader('Chargement des pays...');

        try {
            // Charge tous les pays
            const countries = await this._services.country.getAllCountries();
            this._sharedState.countries = countries;
            console.log(`[GeoCurrency] ${countries.length} pays chargés`);

            // Essaie de récupérer la géolocalisation de l'utilisateur
            this._showLoader('Détection de votre position...');
            try {
                const location = await this._services.geoLocation.getCurrentLocation();
                this._sharedState.userLocation = location;
                console.log('[GeoCurrency] Localisation détectée:', location);
            } catch (geoError) {
                console.warn('[GeoCurrency] Géolocalisation non disponible:', geoError.message);
            }

            // Charge l'historique depuis localStorage
            this._loadHistory();

        } catch (error) {
            console.error('[GeoCurrency] Erreur de chargement:', error);
            this._showNotification('Erreur lors du chargement des données', 'error');
        }
    }

    /**
     * Charge l'historique de conversions
     * @private
     */
    _loadHistory() {
        try {
            const saved = localStorage.getItem('geocurrency-history');
            if (saved) {
                this._sharedState.conversionHistory = JSON.parse(saved);
            }
        } catch (e) {
            console.warn('[GeoCurrency] Impossible de charger l\'historique');
        }
    }

    /**
     * Sauvegarde l'historique
     * @private
     */
    _saveHistory() {
        try {
            localStorage.setItem('geocurrency-history', JSON.stringify(this._sharedState.conversionHistory));
        } catch (e) {
            console.warn('[GeoCurrency] Impossible de sauvegarder l\'historique');
        }
    }

    /**
     * Gère la sélection d'un pays
     * @param {Object} country - Le pays sélectionné
     * @private
     */
    _handleCountrySelect(country) {
        this._sharedState.selectedCountry = country;
        console.log('[GeoCurrency] Pays sélectionné:', country?.name?.common);
    }

    /**
     * Gère le changement de langue fluide
     * @private
     */
    _handleLanguageChange() {
        // 1. Traduire l'interface statique (sidebar, etc.)
        this._localizeUI();
        
        // 2. Rafraîchir la vue actuelle via le router
        if (this._router) {
            this._router.refresh();
        }
    }

    /**
     * Gère la mise à jour de l'historique
     * @param {Array} history - L'historique mis à jour
     * @private
     */
    _handleHistoryUpdate(history) {
        this._sharedState.conversionHistory = history;
        this._saveHistory();
    }

    /**
     * Gère les changements de paramètres
     * @param {Object} settings - Les paramètres modifiés
     * @private
     */
    _handleSettingsChange(settings) {
        if (settings.clearHistory) {
            this._sharedState.conversionHistory = [];
            this._saveHistory();
        }
        if (settings.clearCache) {
            this._services.country.clearCache();
            this._services.exchangeRate.clearCache();
        }
    }

    /**
     * Met à jour l'élément actif de la navigation
     * @private
     */
    _updateActiveNav() {
        const currentPath = window.location.hash.slice(1) || '/map';
        const navItems = document.querySelectorAll('.nav-item');

        navItems.forEach(item => {
            const route = item.getAttribute('data-route');
            if (route === currentPath) {
                item.classList.add('active');
            } else {
                item.classList.remove('active');
            }
        });
    }

    /**
     * Initialise le thème
     * @private
     */
    _initTheme() {
        // Charge le thème sauvegardé
        const savedTheme = localStorage.getItem('geocurrency-theme');
        if (savedTheme) {
            document.documentElement.setAttribute('data-theme', savedTheme);
        } else {
            // Détecte la préférence système
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            if (prefersDark) {
                document.documentElement.setAttribute('data-theme', 'dark');
            }
        }

        // Configure le bouton de thème
        const themeToggle = document.getElementById('theme-toggle');
        if (themeToggle) {
            themeToggle.addEventListener('click', () => this._toggleTheme());
        }
    }

    /**
     * Bascule le thème
     * @private
     */
    _toggleTheme() {
        const html = document.documentElement;
        const currentTheme = html.getAttribute('data-theme');
        const newTheme = currentTheme === 'dark' ? 'light' : 'dark';

        html.setAttribute('data-theme', newTheme);
        localStorage.setItem('geocurrency-theme', newTheme);
    }

    /**
     * Traduit l'interface statique (sidebar, etc.)
     * @private
     */
    _localizeUI() {
        const mapText = document.querySelector('a[data-route="/map"] .nav-text');
        const convText = document.querySelector('a[data-route="/converter"] .nav-text');
        const settingsText = document.querySelector('a[data-route="/settings"] .nav-text');

        if (mapText) mapText.textContent = t('nav_map');
        if (convText) convText.textContent = t('nav_converter');
        if (settingsText) settingsText.textContent = t('nav_settings');

        // On peut aussi mettre à jour le titre de la page si besoin
        // document.title = `GeoCurrency - ${t('app_title')}`; // Si j'avais une clé app_title
    }

    /**
     * Initialise la sidebar
     * @private
     */
    _initSidebar() {
        const sidebar = document.getElementById('sidebar');
        const toggle = document.getElementById('sidebar-toggle');
        const burgerMenu = document.getElementById('burger-menu');
        const backdrop = document.getElementById('mobile-backdrop');
        const navItems = document.querySelectorAll('.nav-item');

        // Desktop sidebar toggle
        if (toggle && sidebar) {
            toggle.addEventListener('click', () => {
                sidebar.classList.toggle('collapsed');
                localStorage.setItem('geocurrency-sidebar', sidebar.classList.contains('collapsed') ? 'collapsed' : 'expanded');
            });

            // Restaure l'état
            const savedState = localStorage.getItem('geocurrency-sidebar');
            if (savedState === 'collapsed') {
                sidebar.classList.add('collapsed');
            }
        }

        // Mobile burger menu
        if (burgerMenu && sidebar && backdrop) {
            const toggleMobileMenu = () => {
                const isOpen = sidebar.classList.contains('mobile-open');
                
                if (isOpen) {
                    sidebar.classList.remove('mobile-open');
                    burgerMenu.classList.remove('active');
                    backdrop.classList.remove('active');
                    document.body.style.overflow = '';
                } else {
                    sidebar.classList.add('mobile-open');
                    burgerMenu.classList.add('active');
                    backdrop.classList.add('active');
                    document.body.style.overflow = 'hidden';
                }
            };

            burgerMenu.addEventListener('click', toggleMobileMenu);
            backdrop.addEventListener('click', toggleMobileMenu);

            // Close menu when navigating
            navItems.forEach(item => {
                item.addEventListener('click', () => {
                    if (window.innerWidth <= 768) {
                        toggleMobileMenu();
                    }
                });
            });
        }

        // Mise à jour initiale de la nav
        setTimeout(() => this._updateActiveNav(), 0);
    }

    /**
     * Affiche le loader
     * @param {string} message - Message à afficher
     * @private
     */
    _showLoader(message = 'Chargement...') {
        const loader = document.getElementById('loader');
        const loaderMessage = document.getElementById('loader-message');

        if (loader) {
            loader.classList.add('visible');
        }
        if (loaderMessage) {
            loaderMessage.textContent = message;
        }
    }

    /**
     * Cache le loader
     * @private
     */
    _hideLoader() {
        const loader = document.getElementById('loader');
        if (loader) {
            loader.classList.remove('visible');
        }
    }

    /**
     * Affiche une notification
     * @param {string} message - Le message
     * @param {string} type - Le type (success, error, warning, info)
     * @private
     */
    _showNotification(message, type = 'info') {
        const container = document.getElementById('notification-container');
        if (!container) return;

        const notification = document.createElement('div');
        notification.className = `notification notification-${type}`;
        notification.innerHTML = `
            <span class="notification-icon">${this._getNotificationIcon(type)}</span>
            <span class="notification-message">${message}</span>
            <button class="notification-close" aria-label="Fermer">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
        `;

        notification.querySelector('.notification-close').addEventListener('click', () => {
            notification.remove();
        });

        container.appendChild(notification);

        // Auto-remove après 5s
        setTimeout(() => {
            if (notification.parentNode) {
                notification.classList.add('fade-out');
                setTimeout(() => notification.remove(), 300);
            }
        }, 5000);
    }

    /**
     * Retourne l'icône pour une notification
     * @param {string} type - Le type
     * @returns {string} - HTML de l'icône
     * @private
     */
    _getNotificationIcon(type) {
        const icons = {
            success: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>',
            error: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>',
            warning: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>',
            info: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>'
        };
        return icons[type] || icons.info;
    }

    /**
     * Affiche une erreur fatale
     * @param {Error} error - L'erreur
     * @private
     */
    _showFatalError(error) {
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
            mainContent.innerHTML = `
                <div class="error-screen">
                    <div class="error-content">
                        <div class="error-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                            </svg>
                        </div>
                        <h1>Erreur</h1>
                        <p>L'application n'a pas pu démarrer correctement.</p>
                        <p class="error-details">${error.message}</p>
                        <button onclick="location.reload()" class="btn btn-primary">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="23 4 23 10 17 10"></polyline>
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                            </svg>
                            Recharger la page
                        </button>
                    </div>
                </div>
            `;
        }
    }

    /**
     * Retourne l'état de l'application
     * @returns {Object} État
     */
    getState() {
        return {
            isReady: this._isReady,
            ...this._sharedState
        };
    }
}

// ============================================
// DÉMARRAGE DE L'APPLICATION
// ============================================

const app = new GeoCurrencyApp();

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => app.init());
} else {
    app.init();
}

// Expose pour le débogage
if (process.env.NODE_ENV !== 'production') {
    window.GeoCurrencyApp = app;
}

export { GeoCurrencyApp };
